import { Button } from '@headlessui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { Dropdown } from '../Dropdown';

export type DropdownOptions = { id: number; value: string; label: string };

interface PaginationProps {
  totalResults: number;
  currentPage: number;
  perPage?: number;
  onPerPageChange: (option: DropdownOptions | null) => void;
  onPageChange: (page: number) => void;
}

function PageNumber({
  totalPages,
  currentPage,
  onPageChange,
}: {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}): JSX.Element {
  return (
    <div className="flex items-center">
      {Array.from({ length: totalPages }, (_, index) => {
        const pageNumber = index + 1;
        const isFirstPage = pageNumber === 1;
        const isLastPage = pageNumber === totalPages;
        const isWithinRange = Math.abs(currentPage - pageNumber) < 3;
        const showEllipsis = !isWithinRange && !isFirstPage && !isLastPage;
        if (isWithinRange || isFirstPage || isLastPage) {
          return (
            <Button
              key={index}
              onClick={() => onPageChange(pageNumber)}
              className={`w-8 h-8 px-2 py-1 border rounded-md text-md ${
                currentPage === pageNumber
                  ? 'border-primary text-primary'
                  : 'border-transparent'
              }`}
            >
              {pageNumber}
            </Button>
          );
        }
        if (showEllipsis && (index === 1 || index === totalPages - 2)) {
          return (
            <span key={index} className="px-4 py-2 rounded-md text-sm">
              <EllipsisHorizontalIcon className="size-4" />
            </span>
          );
        }
        return null;
      })}
    </div>
  );
}
export default function Pagination({
  totalResults,
  currentPage,
  perPage = 10,
  onPerPageChange,
  onPageChange,
}: PaginationProps): JSX.Element {
  const totalPages = Math.ceil(totalResults / perPage);
  const maxPerPage = Math.ceil(totalResults / 10);
  const perPageOptions: DropdownOptions[] = [
    { id: 0, value: '5', label: '5 / page' },
    ...Array.from({ length: maxPerPage }, (_, index) => ({
      id: index + 1,
      value: String((index + 1) * 10),
      label: `${(index + 1) * 10} / page`,
    })),
  ];
  const handlePrevious = (): void => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };
  const handleNext = (): void => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  if (totalPages === 0) return <> </>;
  return (
    <div
      className={`flex items-center gap-2 py-3 ${
        totalPages > 1 ? 'justify-between' : 'justify-end'
      }`}
    >
      {totalPages > 1 && (
        <div className="flex items-center">
          <Button
            onClick={handlePrevious}
            className="w-8 h-8 px-2 py-1 rounded-md"
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
          <PageNumber
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={onPageChange}
          />
          <Button onClick={handleNext} className="w-8 h-8 px-2 py-1 rounded-md">
            <ChevronRightIcon className="size-4" />
          </Button>
        </div>
      )}
      <Dropdown<{ id: number; value: string; label: string }>
        name="per-page"
        options={perPageOptions}
        className="w-[128px]"
        variant="none"
        value={perPageOptions.find(
          (option) => Number(option.value) === perPage,
        )}
        onChange={onPerPageChange}
      />
    </div>
  );
}
